import { api } from './client'

export interface Invoice {
  id: number
  invoiceNumber: string
  invoiceDate: string
  dueDate: string | null
  subtotal: number
  taxRate: number
  taxAmount: number
  /** Omitted entirely by the backend (not null) for a caller without
   * CanSeeRealAmounts (TASK-0087 field-level redaction, both Get and List). */
  total?: number
  status: string
  customerId: number | null
  contractId: number | null
  /** F-149: riferimento al documento in fatturazione (sola lettura). `externalStatus` vale `esito_incerto`
   * dopo un'emissione finita senza risposta. */
  externalStatus?: string | null
  externalNumber?: string | null
  externalDocumentId?: number | null
}

export interface PaymentBatch {
  id: number
  batchCode: string
  status: string
  /** Omitted entirely by the backend (not null) for a caller without
   * CanSeeRealAmounts (TASK-0087 field-level redaction, both Get and List).
   * NOTE (TASK-0090): this type previously declared a nonexistent
   * `totalNetAmount` field that the backend has never returned — the real
   * fields are `totalAmount`/`netAmount`, fixed here as a pre-existing bug
   * unrelated to TASK-0087 (see FinancePage.tsx). */
  totalAmount?: number
  netAmount?: number
  createdAt: string
}

export interface AgentCommission {
  id: number
  agentId: number
  contractId: number | null
  /** Omitted entirely by the backend (not null) for a caller without
   * CanSeeRealAmounts (redacted since TASK-0021). */
  commissionAmount?: number
  status: string
  createdAt: string
}

export function fetchInvoices(
  params?: { page?: number; pageSize?: number; status?: string }
): Promise<{ total: number; page: number; pageSize: number; items: Invoice[] }> {
  const p = new URLSearchParams()
  if (params?.page) p.set('page', String(params.page))
  if (params?.pageSize) p.set('pageSize', String(params.pageSize))
  if (params?.status) p.set('status', params.status)
  const qs = p.toString()
  return api(`/api/invoices${qs ? `?${qs}` : ''}`)
}

export function fetchPaymentBatches(
  params?: { page?: number; pageSize?: number }
): Promise<{ total: number; page: number; pageSize: number; items: PaymentBatch[] }> {
  const p = new URLSearchParams()
  if (params?.page) p.set('page', String(params.page))
  if (params?.pageSize) p.set('pageSize', String(params.pageSize))
  const qs = p.toString()
  return api(`/api/payment-batches${qs ? `?${qs}` : ''}`)
}

export function fetchAgentCommissions(
  params?: { page?: number; status?: string }
): Promise<{ total: number; page: number; pageSize: number; items: AgentCommission[] }> {
  const p = new URLSearchParams()
  if (params?.page) p.set('page', String(params.page))
  if (params?.status) p.set('status', params.status)
  const qs = p.toString()
  return api(`/api/agent-commissions${qs ? `?${qs}` : ''}`)
}

// F-146 (TASK-0400): emissione di una fattura in fatturazione.equalstudio.it. Dietro il flag
// `finanza.emissione_fatturazione`; la rotta e' api/invoices/{id}/external/....
export interface ExternalPreviewResult { valid: boolean; expectedNumber: string | null; validUntil: string }
export interface ExternalDocument {
  documentId: number
  number: string
  status: string
  uuidSdi?: string | null
  sentToSdi?: boolean
  replay: boolean
}

export function previewExternalInvoice(id: number): Promise<ExternalPreviewResult> {
  return api(`/api/invoices/${id}/external/preview`, { method: 'POST' })
}

/** `forceAfterUncertain`: conferma la riemissione dopo un esito incerto (solo FinanceAdministration). */
export function issueExternalInvoice(id: number, forceAfterUncertain = false): Promise<ExternalDocument> {
  return api(`/api/invoices/${id}/external/issue${forceAfterUncertain ? '?forceAfterUncertain=true' : ''}`, {
    method: 'POST', body: JSON.stringify({ confirm: true }),
  })
}

export function refreshExternalInvoice(id: number): Promise<ExternalDocument> {
  return api(`/api/invoices/${id}/external/refresh`, { method: 'POST' })
}

/** Il PDF passa dal CRM (mai un URL diretto di fatturazione). */
export function externalInvoicePdfUrl(id: number): string {
  return `/api/invoices/${id}/external/pdf`
}
